import React, { useCallback, useEffect, useMemo } from "react";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import * as Yup from "yup";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Form,
  FormFeedback,
  Input,
  Label,
  Spinner,
} from "reactstrap";

import {
  getTenantProfile,
  TenantProfile,
  updateTenantProfile,
} from "../../services/tenantProfileService";

interface PublicProfileFormValues {
  public_slug: string;
  instagram_url: string;
  about_text: string;
}

const PublicProfileCard: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState(true);
  const [profile, setProfile] = React.useState<TenantProfile | null>(null);

  const loadProfile = useCallback(async (): Promise<void> => {
    setLoading(true);

    try {
      const response = await getTenantProfile();
      setProfile(response.data);
    } catch (error: unknown) {
      setProfile(null);
      const message =
        typeof error === "object" &&
        error !== null &&
        "data" in error &&
        typeof (error as { data?: unknown }).data === "string"
          ? (error as { data: string }).data
          : t("settings.public_profile.errors.loadFailed");
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        public_slug: Yup.string()
          .trim()
          .max(60, t("settings.public_profile.validation.slugMax"))
          .matches(/^[a-zA-Z0-9_-]*$/, t("settings.public_profile.validation.slugFormat")),
        instagram_url: Yup.string()
          .trim()
          .max(255, t("settings.public_profile.validation.instagramMax"))
          .test(
            "instagram-url",
            t("settings.public_profile.validation.instagramUrl"),
            (value) => {
              if (!value) {
                return true;
              }

              try {
                // eslint-disable-next-line no-new
                new URL(value);
                return true;
              } catch {
                return false;
              }
            }
          ),
        about_text: Yup.string()
          .trim()
          .max(2000, t("settings.public_profile.validation.aboutMax"))
          .nullable()
          .transform((value) => (value === "" ? null : value)),
      }),
    [t]
  );

  const form = useFormik<PublicProfileFormValues>({
    enableReinitialize: true,
    initialValues: {
      public_slug: profile?.public_slug ?? "",
      instagram_url: profile?.instagram_url ?? "",
      about_text: profile?.about_text ?? "",
    },
    validationSchema,
    onSubmit: async (values, helpers) => {
      try {
        const response = await updateTenantProfile({
          public_slug: values.public_slug.trim() === "" ? null : values.public_slug.trim(),
          instagram_url: values.instagram_url.trim() === "" ? null : values.instagram_url.trim(),
          about_text: values.about_text.trim() === "" ? null : values.about_text.trim(),
        });
        setProfile(response.data);
        toast.success(response.message);
        helpers.resetForm({
          values: {
            public_slug: response.data.public_slug ?? "",
            instagram_url: response.data.instagram_url ?? "",
            about_text: response.data.about_text ?? "",
          },
        });
      } catch (error: unknown) {
        toast.error(
          typeof error === "object" &&
            error !== null &&
            "data" in error &&
            typeof (error as { data?: unknown }).data === "string"
            ? (error as { data: string }).data
            : t("settings.public_profile.toast.saveError")
        );
      } finally {
        helpers.setSubmitting(false);
      }
    },
  });

  const miniSiteUrl = useMemo(() => {
    const slug = form.values.public_slug.trim();

    if (!slug) {
      return null;
    }

    if (profile?.mini_site_url && profile.public_slug === slug) {
      return profile.mini_site_url;
    }

    const origin = window.location.origin.replace(/\/$/, "");

    return `${origin}/s/${slug}`;
  }, [form.values.public_slug, profile?.mini_site_url, profile?.public_slug]);

  const copyToClipboard = async (value: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(t("settings.public_profile.toast.copyLinkSuccess"));
    } catch {
      toast.error(t("settings.public_profile.toast.copyError"));
    }
  };

  return (
    <Card className="mb-4">
      <CardHeader className="d-flex align-items-center gap-2">
        <i className="ri-global-line fs-20 text-primary" aria-hidden="true" />
        <h5 className="card-title mb-0">{t("settings.public_profile.title")}</h5>
      </CardHeader>
      <CardBody>
        <p className="text-muted small mb-3">{t("settings.public_profile.description")}</p>

        {loading ? (
          <div className="d-flex justify-content-center py-4">
            <Spinner color="primary" size="sm" />
          </div>
        ) : (
          <Form
            onSubmit={(event) => {
              event.preventDefault();
              form.handleSubmit();
            }}
          >
            <div className="mb-3">
              <Label htmlFor="public-profile-slug" className="form-label">
                {t("settings.public_profile.slug.label")}
              </Label>
              <Input
                id="public-profile-slug"
                name="public_slug"
                type="text"
                placeholder={t("settings.public_profile.slug.placeholder")}
                value={form.values.public_slug}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={!!(form.touched.public_slug && form.errors.public_slug)}
              />
              {form.touched.public_slug && form.errors.public_slug ? (
                <FormFeedback type="invalid">{form.errors.public_slug}</FormFeedback>
              ) : (
                <div className="form-text">{t("settings.public_profile.slug.help")}</div>
              )}
            </div>

            <div className="border rounded-3 p-3 mb-3 bg-light">
              <p className="text-muted small mb-2">{t("settings.public_profile.url.previewLabel")}</p>
              <p className="fw-semibold mb-2">
                {miniSiteUrl ?? t("settings.public_profile.url.empty")}
              </p>
              <Button
                type="button"
                color="soft-success"
                size="sm"
                disabled={!miniSiteUrl}
                onClick={() => {
                  if (miniSiteUrl) {
                    void copyToClipboard(miniSiteUrl);
                  }
                }}
              >
                <i className="ri-link align-bottom me-1" aria-hidden="true" />
                {t("settings.public_profile.actions.copyLink")}
              </Button>
            </div>

            <div className="mb-3">
              <Label htmlFor="public-profile-instagram" className="form-label">
                {t("settings.public_profile.instagram.label")}
              </Label>
              <Input
                id="public-profile-instagram"
                name="instagram_url"
                type="url"
                placeholder={t("settings.public_profile.instagram.placeholder")}
                value={form.values.instagram_url}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={!!(form.touched.instagram_url && form.errors.instagram_url)}
              />
              {form.touched.instagram_url && form.errors.instagram_url ? (
                <FormFeedback type="invalid">{form.errors.instagram_url}</FormFeedback>
              ) : (
                <div className="form-text">{t("settings.public_profile.instagram.help")}</div>
              )}
            </div>

            <div className="mb-3">
              <Label htmlFor="public-profile-about" className="form-label">
                {t("settings.public_profile.about.label")}
              </Label>
              <Input
                id="public-profile-about"
                name="about_text"
                type="textarea"
                rows={4}
                placeholder={t("settings.public_profile.about.placeholder")}
                value={form.values.about_text}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={!!(form.touched.about_text && form.errors.about_text)}
              />
              {form.touched.about_text && form.errors.about_text ? (
                <FormFeedback type="invalid">{form.errors.about_text}</FormFeedback>
              ) : (
                <div className="form-text">{t("settings.public_profile.about.help")}</div>
              )}
            </div>

            <Button type="submit" color="primary" disabled={form.isSubmitting || !form.dirty}>
              {form.isSubmitting ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  {t("settings.public_profile.actions.saving")}
                </>
              ) : (
                t("settings.public_profile.actions.save")
              )}
            </Button>
          </Form>
        )}
      </CardBody>
    </Card>
  );
};

export default PublicProfileCard;
